import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, Users, CalendarDays, Save, BarChart3 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  present: { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Present' },
  absent: { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', label: 'Absent' },
  late: { icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Late' },
  unmarked: { icon: Users, color: 'text-slate-400 bg-slate-50 border-slate-200', label: 'Unmarked' },
};

export default function AttendanceManagement() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('mark');
  const [reportData, setReportData] = useState(null);
  const [reportClass, setReportClass] = useState('');
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');

  useEffect(() => {
    axios.get(`${API}/classes`, { headers }).then(res => setClasses(res.data.data || [])).catch(() => {});
    axios.get(`${API}/attendance/stats`, { headers }).then(res => setStats(res.data.data)).catch(() => {});
  }, []);

  const loadAttendance = async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/attendance?class_id=${selectedClass}&date=${selectedDate}`, { headers });
      setStudents(res.data.data.students || []);
    } catch { toast.error('Failed to load attendance'); }
    setLoading(false);
  };

  useEffect(() => { if (selectedClass && selectedDate) loadAttendance(); }, [selectedClass, selectedDate]);

  const updateStatus = (studentId, status) => {
    setStudents(prev => prev.map(s => s.student_id === studentId ? { ...s, status } : s));
  };

  const markAll = (status) => {
    setStudents(prev => prev.map(s => ({ ...s, status })));
  };

  const handleSave = async () => {
    const entries = students.filter(s => s.status !== 'unmarked').map(s => ({
      student_id: s.student_id, status: s.status,
    }));
    if (entries.length === 0) return toast.error('Mark at least one student');
    setSaving(true);
    try {
      await axios.post(`${API}/attendance/mark`, { class_id: selectedClass, date: selectedDate, entries }, { headers });
      toast.success(`${entries.length} attendance records saved`);
      axios.get(`${API}/attendance/stats`, { headers }).then(res => setStats(res.data.data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const loadReport = async () => {
    if (!reportClass || !reportFrom || !reportTo) return toast.error('Select class and date range');
    try {
      const res = await axios.get(`${API}/attendance/report?class_id=${reportClass}&from_date=${reportFrom}&to_date=${reportTo}`, { headers });
      setReportData(res.data.data);
    } catch { toast.error('Failed to load report'); }
  };

  const present = students.filter(s => s.status === 'present').length;
  const absent = students.filter(s => s.status === 'absent').length;
  const late = students.filter(s => s.status === 'late').length;
  const unmarked = students.filter(s => s.status === 'unmarked').length;

  return (
    <div className="space-y-6" data-testid="attendance-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="attendance-title">Attendance</h1>
          <p className="text-sm text-slate-500 mt-1">Mark daily student attendance and view reports</p>
        </div>
      </div>

      {/* Stats */}
      {stats?.today && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-400">Total Students</p><p className="text-2xl font-bold text-slate-900">{stats.today.total_students}</p></div>
          <div className="bg-white rounded-xl border border-emerald-100 p-4"><p className="text-xs text-emerald-400">Present Today</p><p className="text-2xl font-bold text-emerald-600">{stats.today.present}</p></div>
          <div className="bg-white rounded-xl border border-red-100 p-4"><p className="text-xs text-red-400">Absent Today</p><p className="text-2xl font-bold text-red-600">{stats.today.absent}</p></div>
          <div className="bg-white rounded-xl border border-amber-100 p-4"><p className="text-xs text-amber-400">Late Today</p><p className="text-2xl font-bold text-amber-600">{stats.today.late}</p></div>
          <div className="bg-white rounded-xl border border-blue-100 p-4"><p className="text-xs text-blue-400">Attendance %</p><p className="text-2xl font-bold text-blue-600">{stats.today.percentage}%</p></div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {[{ key: 'mark', label: 'Mark Attendance' }, { key: 'report', label: 'Reports' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-md ${tab === t.key ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Mark Attendance Tab */}
      {tab === 'mark' && (
        <div className="space-y-4 mt-6">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="w-48">
              <p className="text-xs text-slate-500 mb-1">Class</p>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger data-testid="attendance-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Date</p>
              <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-44" data-testid="attendance-date-input" />
            </div>
            {students.length > 0 && (
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" size="sm" onClick={() => markAll('present')} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid="mark-all-present"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> All Present</Button>
                <Button variant="outline" size="sm" onClick={() => markAll('absent')} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="mark-all-absent"><XCircle className="h-3.5 w-3.5 mr-1" /> All Absent</Button>
              </div>
            )}
          </div>

          {!selectedClass ? (
            <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">Select a class to mark attendance</p>
            </div>
          ) : loading ? (
            <div className="text-center py-16 text-slate-400">Loading students...</div>
          ) : students.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <p className="text-slate-400">No students found in this class</p>
            </div>
          ) : (
            <>
              {/* Summary bar */}
              <div className="flex gap-4 text-sm">
                <span className="text-emerald-600 font-semibold">{present} Present</span>
                <span className="text-red-600 font-semibold">{absent} Absent</span>
                <span className="text-amber-600 font-semibold">{late} Late</span>
                <span className="text-slate-400">{unmarked} Unmarked</span>
              </div>

              <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
                <table className="w-full" data-testid="attendance-table">
                  <thead><tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 w-12">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Student</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Adm No</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {students.map((s, i) => {
                      const cfg = STATUS_CONFIG[s.status];
                      return (
                        <tr key={s.student_id} className="hover:bg-slate-50/50">
                          <td className="px-4 py-2.5 text-sm text-slate-400">{i + 1}</td>
                          <td className="px-4 py-2.5 text-sm font-medium text-slate-900">{s.full_name}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{s.admission_number}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-center gap-1.5">
                              {['present', 'absent', 'late'].map(status => {
                                const sc = STATUS_CONFIG[status];
                                const Icon = sc.icon;
                                const active = s.status === status;
                                return (
                                  <button key={status} onClick={() => updateStatus(s.student_id, status)}
                                    className={`h-8 px-3 rounded-lg border text-xs font-semibold flex items-center gap-1 transition-all ${active ? sc.color : 'text-slate-300 bg-white border-slate-100 hover:border-slate-200'}`}
                                    data-testid={`att-${s.student_id}-${status}`}>
                                    <Icon className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">{sc.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white px-8" data-testid="save-attendance-btn">
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Attendance'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Reports Tab */}
      {tab === 'report' && (
        <div className="space-y-4 mt-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Attendance Report</h3>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="w-48">
                <p className="text-xs text-slate-500 mb-1">Class</p>
                <Select value={reportClass} onValueChange={setReportClass}>
                  <SelectTrigger data-testid="report-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><p className="text-xs text-slate-500 mb-1">From</p><Input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} className="w-40" data-testid="report-from-date" /></div>
              <div><p className="text-xs text-slate-500 mb-1">To</p><Input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} className="w-40" data-testid="report-to-date" /></div>
              <Button onClick={loadReport} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="load-report-btn">
                <BarChart3 className="h-4 w-4 mr-2" /> Generate
              </Button>
            </div>
          </div>

          {reportData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-slate-400">Total Records</p><p className="text-xl font-bold">{reportData.summary.total}</p></div>
                <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4"><p className="text-xs text-emerald-500">Present</p><p className="text-xl font-bold text-emerald-600">{reportData.summary.present}</p></div>
                <div className="bg-red-50 rounded-xl border border-red-100 p-4"><p className="text-xs text-red-500">Absent</p><p className="text-xl font-bold text-red-600">{reportData.summary.absent}</p></div>
                <div className="bg-amber-50 rounded-xl border border-amber-100 p-4"><p className="text-xs text-amber-500">Late</p><p className="text-xl font-bold text-amber-600">{reportData.summary.late}</p></div>
                <div className="bg-blue-50 rounded-xl border border-blue-100 p-4"><p className="text-xs text-blue-500">Attendance %</p><p className="text-xl font-bold text-blue-600">{reportData.summary.percentage}%</p></div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
